import {
    Autocomplete,
    Box,
    FilterOptionsState,
    TextField,
    Typography,
} from '@mui/material'
import { disableFieldCheck } from '../../services/validation/address-employment-form/disableFieldCheck'
import { HTMLAttributes, ReactNode, SyntheticEvent, useState, useEffect } from 'react'
import { CompanyOption, NEW_COMPANY_CODE } from '../../services/ntuc/userService/company'
import { capitaliseText } from '../../helpers/StringHelpers'

export interface DropdownSearchProps {
    title: string
    defaultValue: string
    searching: boolean
    options: CompanyOption[]
    showError: boolean
    errorMessage: string
    onChange: (value: string) => void
    onSelect: (option: CompanyOption) => void
    onBlur: (value: string) => void
    dependentField?: string
    disabled?: boolean
}

export interface SearchOption {
    title: string
    inputValue: string
    item: CompanyOption
    userInput: boolean
}

export function DropDownSearch(props: DropdownSearchProps) {
    const {
        title,
        defaultValue,
        searching,
        options,
        showError,
        errorMessage,
        onChange,
        onSelect,
        onBlur,
        dependentField,
        disabled,
    } = props

    const [value, setValue] = useState(defaultValue)
    const [inputValue, setInputValue] = useState(``)

    useEffect(() => {
        if (disableFieldCheck.has(dependentField ?? ``)) {
            setValue(``)
        }
        if (defaultValue !== null && defaultValue !== undefined) {
            setValue(defaultValue)
        }
    }, [dependentField, defaultValue])

    const handleInputOnChange = (event: SyntheticEvent<Element, Event>, newValue: string) => {
        setInputValue(capitaliseText(newValue))
        onChange(capitaliseText(newValue))
    }

    const handleValueOnSelect = (event: SyntheticEvent<Element, Event>,
        newValue: string | SearchOption | null) => {
        if (typeof newValue === `string`) {
            const option: CompanyOption = { code: NEW_COMPANY_CODE, name: newValue, businessNature: `` }
            setValue(newValue)
            onSelect(option)
        } else if (newValue) {
            if (newValue.userInput) {
                setValue(newValue.inputValue)
            } else {
                setValue(newValue.title)
            }
            onSelect(newValue.item)
        } else if (newValue === null) {
            setValue(``)
            onSelect({ code: ``, name: ``})
        }
    }

    const handleFilterOptions = (options: SearchOption[], params: FilterOptionsState<SearchOption>) => {
        if (searching == true) {
            return []
        }
        if (params.inputValue === ``) {
            return options
        }
        const companyItem: CompanyOption = { code: NEW_COMPANY_CODE, name: params.inputValue, businessNature: `` }
        const noFoundOption: SearchOption = {
            title: params.inputValue,
            inputValue: params.inputValue,
            item: companyItem,
            userInput: true,
        }
        if (options[0] && options[0].title === params.inputValue) {
            return options
        }
        return [noFoundOption, ...options]
    }

    const handleOptionLabel = (option: SearchOption | string) => {
        return typeof option === `string` ? option : option.title
    }

    const loadingText = `Searching...`

    const searchOptions: SearchOption[] = options.map(item => {
        const searchOption: SearchOption = {
            title: item.name,
            inputValue: ``,
            item: item,
            userInput: false,
        }
        return searchOption
    })

    const handleRenderOption = (props: HTMLAttributes<HTMLLIElement>, option: SearchOption): ReactNode => {
        if (option.userInput) {
            return (
                <Box component="li" sx={{ borderBottom: `1px solid`, borderColor: `primary`, mb: 1 }} {...props} key={option.item.code}>
                    Not found. Try a different search or&nbsp;<Typography variant="body1" color="primary"><u>use &quot;{option.title}&quot;</u></Typography>
                </Box>
            )
        }

        const search = option.title.replace(inputValue, `<strong>${inputValue}</strong>`)
        return <Box component="li" sx={{ borderBottom: `1px`, borderColor: `secondary` }} dangerouslySetInnerHTML={{ __html: `<li>${search}</li>` }} {...props} key={option.item.code}></Box>
    }

    const handleOptionEqualToValue = (option: SearchOption, value: SearchOption) => {
        return option.inputValue === value.inputValue
    }

    const checkDisabled = () => {
        if (disableFieldCheck.has(dependentField ?? ``)) {
            return true
        }
        return false
    }

    const handleOnBlur = () => {
        setInputValue(value)
        onBlur(value)
    }

    return (
        <Autocomplete
            disablePortal
            options={searchOptions}
            value={value}
            onChange={handleValueOnSelect}
            inputValue={inputValue}
            freeSolo={true}
            loading={searching}
            loadingText={loadingText}
            renderOption={handleRenderOption}
            filterOptions={handleFilterOptions}
            getOptionLabel={handleOptionLabel}
            onInputChange={handleInputOnChange}
            isOptionEqualToValue={handleOptionEqualToValue}
            disabled={disabled}
            hidden={checkDisabled()}
            renderInput={(params) => (
                <TextField
                    error={showError}
                    helperText={errorMessage}
                    {...params}
                    label={title}
                    onBlur={handleOnBlur} />
            )}
        />
    )
}